import { supabase } from './supabaseClient';

/**
 * Fetch merged telemetry for an EPCIS shipment record.
 * Combines gps_tracks, sensor_readings, and gateway_sensor_readings (MQTT path).
 * Respects custody legs: sender data until handoff cutover, then receiver zone/gateway.
 */
export async function fetchShipmentTelemetry(shipment, handoffs = [], gatewayBindings = null) {
  if (!shipment?.id) return [];

  let bindings = gatewayBindings;
  if (!bindings) {
    try {
      bindings = await listShipmentGatewayBindings(shipment.id);
    } catch {
      bindings = [];
    }
  }

  const sortedHandoffs = [...(handoffs || [])].sort(
    (a, b) => new Date(a.handoff_at) - new Date(b.handoff_at),
  );

  const legs = buildTelemetryLegs(shipment, sortedHandoffs, bindings);
  const results = await Promise.all(legs.map((leg) => fetchLegTelemetry(leg)));
  const merged = results.flat();

  const byKey = new Map();
  merged.forEach((row) => {
    const key = `${row.recorded_at}:${row.sensor_id || ''}:${row.source}`;
    if (!byKey.has(key)) byKey.set(key, row);
  });

  return Array.from(byKey.values()).sort(
    (a, b) => new Date(a.recorded_at) - new Date(b.recorded_at),
  );
}

function resolveInitialGatewayId(gatewayBindings) {
  if (!gatewayBindings?.length) return null;
  const sorted = [...gatewayBindings].sort(
    (a, b) => new Date(a.effective_from) - new Date(b.effective_from),
  );
  return sorted[0]?.gateway_id || null;
}

function buildTelemetryLegs(shipment, handoffs, gatewayBindings = []) {
  if (!handoffs.length) {
    return [{
      sensorIds: shipment.sensor_id ? [shipment.sensor_id] : [],
      zoneId: shipment.zone_id,
      gatewayId: resolveInitialGatewayId(gatewayBindings),
      from: null,
      to: null,
    }];
  }

  const legs = [];
  let fromTime = shipment.started_at || null;

  handoffs.forEach((handoff) => {
    const toTime = handoff.telemetry_cutover_at || handoff.handoff_at;
    const sensorIds = handoff.from_sensor_id ? [handoff.from_sensor_id] : [];

    legs.push({
      sensorIds,
      zoneId: handoff.from_zone_id,
      gatewayId: handoff.from_gateway_id,
      from: fromTime,
      to: toTime,
    });

    fromTime = toTime;
  });

  const lastHandoff = handoffs[handoffs.length - 1];
  const isZoneContinuation = lastHandoff.custody_mode === 'device_retained_sender';
  const activeSensorIds = isZoneContinuation
    ? []
    : (lastHandoff.to_sensor_id
      ? [lastHandoff.to_sensor_id]
      : shipment.sensor_id ? [shipment.sensor_id] : []);

  legs.push({
    sensorIds: activeSensorIds,
    zoneId: lastHandoff.to_zone_id || shipment.zone_id,
    gatewayId: lastHandoff.to_gateway_id,
    from: fromTime,
    to: null,
    custodyMode: lastHandoff.custody_mode,
    includeZoneSensors: isZoneContinuation,
  });

  return legs;
}

async function fetchLegTelemetry(leg) {
  const rows = [];
  const sensorIds = [...(leg.sensorIds || [])];

  if (leg.includeZoneSensors && leg.zoneId) {
    const { data: zoneSensors } = await supabase
      .from('sensors')
      .select('id')
      .eq('zone_id', leg.zoneId);

    (zoneSensors || []).forEach((s) => {
      if (!sensorIds.includes(s.id)) sensorIds.push(s.id);
    });
  }

  if (!sensorIds.length) return rows;

  const gpsRows = await queryTable('gps_tracks', sensorIds, leg);
  gpsRows.forEach((row) => rows.push({ ...row, source: 'gps_tracks' }));

  const sensorRows = await queryTable('sensor_readings', sensorIds, leg, 'recorded_at');
  sensorRows.forEach((row) => rows.push({
    ...row,
    latitude: null,
    longitude: null,
    source: 'sensor_readings',
  }));

  let gwQuery = supabase
    .from('gateway_sensor_readings')
    .select('temperature, humidity, timestamp, sensor_id, gateway_id')
    .in('sensor_id', sensorIds)
    .order('timestamp', { ascending: true });

  if (leg.gatewayId) gwQuery = gwQuery.eq('gateway_id', leg.gatewayId);
  if (leg.from) gwQuery = gwQuery.gte('timestamp', leg.from);
  if (leg.to) gwQuery = gwQuery.lte('timestamp', leg.to);

  const { data: gwRows } = await gwQuery.limit(500);
  (gwRows || []).forEach((row) => rows.push({
    ...row,
    recorded_at: row.timestamp,
    latitude: null,
    longitude: null,
    source: 'gateway_mqtt',
  }));

  return rows;
}

async function queryTable(table, sensorIds, leg, timestampCol = 'recorded_at') {
  let query = supabase
    .from(table)
    .select(`temperature, humidity, latitude, longitude, ${timestampCol}, sensor_id, gateway_id`)
    .in('sensor_id', sensorIds)
    .order(timestampCol, { ascending: true });

  if (leg.from) query = query.gte(timestampCol, leg.from);
  if (leg.to) query = query.lte(timestampCol, leg.to);

  const { data, error } = await query.limit(500);
  if (error) {
    console.warn(`fetchShipmentTelemetry: ${table} query failed`, error);
    return [];
  }
  return (data || []).map((row) => ({
    ...row,
    recorded_at: row[timestampCol] || row.recorded_at,
  }));
}

export async function listShipmentGatewayBindings(shipmentId) {
  const { data, error } = await supabase
    .from('shipment_gateway_bindings')
    .select('*')
    .eq('shipment_id', shipmentId)
    .order('effective_from', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getActiveShipmentGatewayBinding(shipmentId) {
  const { data, error } = await supabase
    .from('shipment_gateway_bindings')
    .select('*')
    .eq('shipment_id', shipmentId)
    .is('effective_to', null)
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function bindShipmentGateway({
  shipmentId,
  gatewayId,
  sensorId,
  zoneId,
  bindingSource = 'gateway',
  handoffId,
}) {
  const { data, error } = await supabase.rpc('bind_shipment_gateway', {
    p_shipment_id: shipmentId,
    p_gateway_id: gatewayId || null,
    p_sensor_id: sensorId || null,
    p_zone_id: zoneId || null,
    p_binding_source: bindingSource,
    p_handoff_id: handoffId || null,
  });

  if (error) throw error;
  return data;
}

export async function ensureInitialShipmentBinding(shipmentId) {
  const { data, error } = await supabase.rpc('ensure_initial_shipment_binding', {
    p_shipment_id: shipmentId,
  });

  if (error) throw error;
  return data;
}

export async function getActiveShipmentForSensor(sensorId) {
  if (!sensorId) return null;

  const { data, error } = await supabase
    .from('shipment_gateway_bindings')
    .select('*, shipments(*)')
    .eq('sensor_id', sensorId)
    .is('effective_to', null)
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.shipments || null;
}
